<template>
  <div class="price">
    <span :class="priceClass">
      {{ priceCurrent }}
    </span>
    <span
      v-if="props.price?.unit_string"
      class="price-for"
    >
      {{ ' ' + props.price.unit_string }}
    </span>
    <div
      v-if="props.price?.discount"
      class="discount"
    >
      <div class="highlight">
        <span
          v-if="props.price?.discount?.value"
          class="price-percentage"
          >{{ percentageString }}</span
        >
        <span
          v-if="props.price?.discount?.expiration_date"
          class="price-discount-expiration"
        >
          {{ priceDiscountInspiration }}
        </span>
      </div>
      <span class="price-old">{{ priceOld }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { ExperiencePrice } from '@/shared/api/experience';
  import { discountExpirationToDate } from '@experience/datetime-utils';
  import { formatMoney } from '@experience/money-utils';

  interface Props {
    price: ExperiencePrice;
  }

  const props = defineProps<Props>();

  const priceClass = computed(() => ['price-current', { 'price-discount': props.price?.discount }]);

  const percentageString = computed(() => {
    const discount = props.price?.discount;
    const value = discount ? discount.value : 0;
    const percentage = Number.parseInt(`${value * 100}`, 10);

    return `–${percentage}%`;
  });

  const priceDiscountInspiration = computed(() =>
    props.price?.discount?.expiration_date ? discountExpirationToDate(props.price?.discount?.expiration_date) : '',
  );
  const priceOld = computed(() =>
    props.price?.discount?.original_price && props.price?.currency
      ? formatMoney(props.price?.discount.original_price, props.price?.currency)
      : '',
  );
  const priceCurrent = computed(() =>
    props.price?.value && props.price?.currency ? formatMoney(props.price?.value, props.price?.currency) : '',
  );
</script>

<style lang="scss" scoped>
  .price {
    display: inline;
    color: var(--alias-color-text-inverse);
    font-size: 14px;
  }

  .discount {
    display: flex;
    flex-direction: row;
    gap: 4px;
    align-items: center;

    .highlight {
      padding: 0 4px;
      border-radius: 2px;
      background: var(--alias-color-text-accent-red);
      font-size: 12px;
      line-height: 16px;
    }

    .price-old {
      position: relative;
      color: var(--alias-color-text-subtle);

      &:before {
        content: '';
        position: absolute;
        top: 50%;
        right: 0;
        width: 100%;
        border-bottom: 1px solid var(--alias-color-text-subtle);
        transform: skewY(8deg);
      }
    }
  }
</style>
